import debounce from 'lodash/debounce';

import WssService from '~/services/WssService';

import type { InferActionsType } from '~/Reducers';
import type { CLAN_WARS_STAGE, IClanWarsState } from '~/Reducers/ReducerClanWars';
import type { ISocketError, WSS_ACTIONS } from '~/services/WssService';
import type { Ship } from '~/types/declaration';
import type { Nullable } from '~/types/generics';

const GET_RATINGS = 'GET_RATINGS';
const RATINGS_AUTOCOMPLETE = 'RATINGS_AUTOCOMPLETE';
const RATINGS_SEARCH = 'RATINGS_SEARCH';
export const CHECK_FROM_PARAM = 'CHECK_FROM_PARAM';
export const CHECK_READY = 'CHECK_READY';
export const SET_FETCHING = 'SET_FETCHING';
export const CLEAR_CLAN_WARS_DATA = 'CLEAR_CLAN_WARS_DATA';
export const CLEAR_SEARCH = 'CLEAR_SEARCH';
export const GET_SHIPS = 'GET_SHIPS';
export const HIDE_LAST_ROUND_RESULT = 'HIDE_LAST_ROUND_RESULT';
export const INIT = 'INIT';
export const SEARCH_INPUT_CHANGE = 'SEARCH_INPUT_CHANGE';
export const SELECT_RATING_LEAGUE = 'SELECT_RATING_LEAGUE';
export const SET_ERROR = 'SET_ERROR';
export const SET_GRADES_TABLE_TAB = 'SET_GRADES_TABLE_TAB';
export const SET_HISTORY_IS_FETCHING = 'SET_HISTORY_IS_FETCHING';
export const SET_OFFLINE = 'SET_OFFLINE';
export const SET_ONLINE = 'SET_ONLINE';
export const SET_PREPARE_STAGE_TAB = 'SET_PREPARE_STAGE_TAB';
export const SET_SELECTED_CLAN_ID = 'SET_SELECTED_CLAN_ID';
export const SET_USE_ATTEMPTS = 'SET_USE_ATTEMPTS';
export const SHOW_LAST_ROUND_RESULT = 'SHOW_LAST_ROUND_RESULT';
export const SLIDE_HISTORY_MODAL = 'SLIDE_HISTORY_MODAL';
export const TOGGLE_AUTO_PARTICIPATION = 'TOGGLE_AUTO_PARTICIPATION';
export const TOGGLE_PLAYERS_COUNT_IN_RATING = 'TOGGLE_PLAYERS_COUNT_IN_RATING';
export const UNSET_HISTORY_MODAL_DATA = 'UNSET_HISTORY_MODAL_DATA';

const debouncedAutocomplete = debounce((q: string) => {
  WssService.ratingsAutocomplete(q);
}, 500);

export type ActionsType = InferActionsType<typeof actionsClanWars>;

type ActionsOnMessage =
  | {
      readonly type: WSS_ACTIONS.CWARS_UPDATE_SETTINGS;
      readonly payload: IClanWarsState['warSettings'] & {
        currentRound: number;
        roundType: IClanWarsState['roundType'];
        roundGroupSize: IClanWarsState['roundGroupSize'];
      };
    }
  | {
      readonly type: WSS_ACTIONS.CWARS_UPDATE_LAST_ROUND_RESULTS;
      readonly payload: IClanWarsState['lastRoundResults'];
    }
  | {
      readonly type: WSS_ACTIONS.CWARS_UPDATE_STAGES;
      readonly payload: {
        errors: unknown;
        stages: IClanWarsState['stages'];
        currentStage: IClanWarsState['stages']['currentStage'];
        restrictions: IClanWarsState['stages']['restrictions'];
      };
    }
  | {
      readonly type: WSS_ACTIONS.CWARS_UPDATE_RATINGS;
      readonly payload: IClanWarsState['clanRatingList'];
    }
  | {
      readonly type: WSS_ACTIONS.CWARS_UPDATE_ADDITIONAL_ATTEMPTS;
      readonly payload: IClanWarsState;
    }
  | {
      readonly type: WSS_ACTIONS.CWARS_UPDATE_RATINGS_SEARCH;
      readonly payload: IClanWarsState['clanRatingSearchList'];
    }
  | {
      readonly type: WSS_ACTIONS.CWARS_UPDATE_RATINGS_SEARCH_AUTOCOMPLETE;
      readonly payload: IClanWarsState['clanRatingAutocomplete'];
    }
  | {
      readonly type: WSS_ACTIONS.CWARS_UPDATE_COMMON_INFO;
      readonly payload: IClanWarsState['commonInfo'];
    }
  | {
      readonly type: WSS_ACTIONS.CWARS_UPDATE_ENEMY_GRADES;
      readonly payload: IClanWarsState['enemyGrades'];
    }
  | {
      readonly type: WSS_ACTIONS.CWARS_UPDATE_ALLIES_ATTEMPTS;
      readonly payload: IClanWarsState['stages'][CLAN_WARS_STAGE.WAR]['alliesAttempts'];
    }
  | {
      readonly type: WSS_ACTIONS.CWARS_STAGE_START;
      readonly payload: {
        currentStage: CLAN_WARS_STAGE;
        roundType: IClanWarsState['roundType'];
        groupSize: IClanWarsState['roundGroupSize'];
      };
    }
  | {
      readonly type: WSS_ACTIONS.CWARS_UPDATE_ROUNDS_RESULTS_HISTORY;
      readonly payload: {
        items: IClanWarsState['roundsHistory'];
        count: IClanWarsState['roundsHistoryCount'];
        offset: IClanWarsState['roundsHistoryOffset'];
        limit: IClanWarsState['roundsHistoryLimit'];
      };
    };

export const actionsClanWars = {
  setWssData: (type: unknown, payload: unknown) =>
    ({
      type: type,
      payload,
    }) as ActionsOnMessage,

  setOnline: () =>
    ({
      type: SET_ONLINE,
    }) as const,

  toggleAutoParticipation: () =>
    ({
      type: TOGGLE_AUTO_PARTICIPATION,
    }) as const,

  setUseAttempts: (val: boolean) =>
    ({
      type: SET_USE_ATTEMPTS,
      payload: { val },
    }) as const,

  clearClanWarsData: () =>
    ({
      type: CLEAR_CLAN_WARS_DATA,
    }) as const,

  setOffline: () =>
    ({
      type: SET_OFFLINE,
    }) as const,

  togglePlayersCountInRating: () =>
    ({
      type: TOGGLE_PLAYERS_COUNT_IN_RATING,
    }) as const,

  setSelectedClanId: (clanId: Nullable<number> = null) =>
    ({
      type: SET_SELECTED_CLAN_ID,
      payload: { clanId: clanId },
    }) as const,

  slideHistoryModal: (direction: 'left' | 'right') =>
    ({
      type: SLIDE_HISTORY_MODAL,
      payload: { direction },
    }) as const,

  showLastRoundResults: () =>
    ({
      type: SHOW_LAST_ROUND_RESULT,
    }) as const,

  unsetHistoryModalData: () =>
    ({
      type: UNSET_HISTORY_MODAL_DATA,
    }) as const,

  setHistoryIsFetching: (isFetching: boolean) =>
    ({
      type: SET_HISTORY_IS_FETCHING,
      payload: { isFetching },
    }) as const,

  init: () =>
    ({
      type: INIT,
    }) as const,

  getShip: (ships: Record<string, Ship>) =>
    ({
      type: GET_SHIPS,
      payload: { ships },
    }) as const,

  setError: (error: ISocketError | Error) =>
    ({
      type: SET_ERROR,
      payload: { error },
    }) as const,

  checkFromParam: (isFromPort: boolean) =>
    ({
      type: CHECK_FROM_PARAM,
      payload: { isFromPort },
    }) as const,

  checkReady: (isReady: boolean) =>
    ({
      type: CHECK_READY,
      payload: { isReady },
    }) as const,

  setFetching: (isFetching: boolean) =>
    ({
      type: SET_FETCHING,
      payload: { isFetching },
    }) as const,

  clearSearch: () =>
    ({
      type: CLEAR_SEARCH,
    }) as const,

  getRating: () =>
    ({
      type: GET_RATINGS,
    }) as const,

  selectRatingLeague: (league: Nullable<number> = null) =>
    ({
      type: SELECT_RATING_LEAGUE,
      payload: { league },
    }) as const,

  searchInputChange: (value: string) =>
    ({
      type: SEARCH_INPUT_CHANGE,
      payload: { value },
    }) as const,

  ratingsSearch: (query: string, offset?: number, limit?: number) => {
    WssService.ratingsSearch(query, offset, limit);
    return {
      type: RATINGS_SEARCH,
    } as const;
  },

  ratingsAutocomplete: (query: string) => {
    debouncedAutocomplete(query);
    return {
      type: RATINGS_AUTOCOMPLETE,
    } as const;
  },

  hideLastRoundResults: () =>
    ({
      type: HIDE_LAST_ROUND_RESULT,
    }) as const,

  setPrepareStageTab: (prepageStageTabIndex: number) =>
    ({
      type: SET_PREPARE_STAGE_TAB,
      payload: { prepageStageTabIndex },
    }) as const,

  setGradesTableTab: (gradesTableTabIndex: number) => {
    if (gradesTableTabIndex > 0) {
      WssService.getEnemyGrades();
    }
    return {
      type: SET_GRADES_TABLE_TAB,
      payload: {
        gradesTableTabIndex,
      },
    } as const;
  },
};
